/**
 * SDK 房券列表(特价预售模块)
 *
 * 不复用主站 VoucherListSection/VoucherPresetCard(依赖 react-query + Next.js Link)。
 * 镜像其布局与展示逻辑,SDK 风格:手动 useEffect 拉取 + cancelled 标志忽略卸载后响应
 * (参考 CouponBanner;房券列表挂载即加载一次,无日期切换,不需 PackageList 的 epoch 计数),
 * openUrl 跳转(非 Link),hh-* 类名。loading/失败/空均不渲染(返回 null,避免页面抖动,同 CouponBanner)。
 *
 * 数据走 SDK fetchVoucherList(模块级缓存,会话内切日期不重复打 BFF;房券列表不依赖日期)。
 * 点整张卡片 -> openUrl /vouchers/{id}(详情);点「抢购」-> openUrl /vouchers/{id}/booking(提单)。
 * 不派发 book 事件:房券非套餐,BookPayload.packageId 语义为套餐 ID,房券走独立跳转
 * (与主站 VoucherPresetCard 一致:仅 Link 跳转,不派发 book)。
 */
import { useEffect, useState, type KeyboardEvent } from 'react';
import type { Voucher } from '@/types/voucher';
import { fetchVoucherList, getVoucherPriceUnit } from '../api/voucher';
import { openUrl } from './shared';
import { point } from '../statistic';

/** 默认可见卡片数(超出展开/收起),对齐主站 VoucherListSection */
const DEFAULT_VISIBLE_COUNT = 3;

const CheckCircleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21.801 10A10 10 0 1 1 17 3.335" />
    <path d="m9 11 3 3L22 4" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m18 15-6-6-6 6" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

/** 单张房券卡(对齐主站 VoucherPresetCard 视觉) */
function VoucherCard({ voucher }: { voucher: Voucher }) {
  const hasRefundHint = voucher.returnPolicy === 2;
  const hasCouponPrice = voucher.afterCouponPrice != null && voucher.afterCouponPrice > 0;

  const openDetail = () => openUrl(`/vouchers/${voucher.id}`, {});
  const openBooking = () => openUrl(`/vouchers/${voucher.id}/booking`, {});

  // 整卡可点进详情(键盘 Enter/Space 同样触发);「抢购」按钮 stopPropagation 走提单,不冒泡误触详情
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openDetail();
    }
  };

  return (
    <article
      className="hh-voucher__card"
      role="button"
      tabIndex={0}
      aria-label={`查看「${voucher.name}」详情`}
      onClick={openDetail}
      onKeyDown={onKeyDown}
    >
      <div className="hh-voucher__card-body">
        {/* 左侧图片 */}
        <div className="hh-voucher__media">
          {voucher.images[0] ? (
            // SDK 独立 bundle 脱离 Next 运行时,无法用 next/image,用原生 img
            // eslint-disable-next-line @next/next/no-img-element
            <img
              className="hh-voucher__img"
              src={voucher.images[0]}
              alt={voucher.name}
              draggable={false}
            />
          ) : (
            <div className="hh-voucher__img hh-voucher__img--empty" />
          )}
        </div>

        {/* 右侧内容 */}
        <div className="hh-voucher__main">
          <div>
            {/* 标题行:特价预售标签 + 标题(整卡可点进详情,此为视觉单元)+ 右箭头 + 已售数量(右侧) */}
            <div className="hh-voucher__title-row">
              <span className="hh-voucher__tag">特价预售</span>
              <div className="hh-voucher__title-btn">
                <h3 className="hh-voucher__name">{voucher.name}</h3>
                <ArrowRightIcon />
              </div>
              {voucher.paidCount ? (
                <span className="hh-voucher__sold">已售 {voucher.paidCount}</span>
              ) : null}
            </div>

            {/* 卖点简介 */}
            {voucher.brief && <p className="hh-voucher__brief">{voucher.brief}</p>}

            {/* 退款标签 */}
            {hasRefundHint && <p className="hh-voucher__refund">未预约全额退 · 过期自动退</p>}
          </div>

          {/* 底部价格与按钮 */}
          <div className="hh-voucher__foot">
            <div className="hh-voucher__price">
              {hasCouponPrice && <span className="hh-voucher__price-badge">券后价</span>}
              <span className="hh-voucher__price-symbol">¥</span>
              <span className="hh-voucher__price-num">{voucher.price}</span>
              <span className="hh-voucher__price-unit">
                /{voucher.nights > 1 ? voucher.nights : ''}
                {getVoucherPriceUnit(voucher.buyLimitType)}
              </span>
            </div>
            <button
              type="button"
              className="hh-voucher__book"
              onClick={(e) => {
                e.stopPropagation();
                openBooking();
              }}
            >
              抢购
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

/** 房券列表模块(特价预售)。loading/失败/空均不渲染。 */
export function VoucherList({ pageName }: { pageName: string }) {
  const [vouchers, setVouchers] = useState<Voucher[] | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const list = await fetchVoucherList();
      if (!cancelled) setVouchers(list);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // 加载中(首次未命中缓存)/失败(null)/空列表:均不渲染,避免占位抖动
  if (!vouchers || vouchers.length === 0) return null;

  const hasMore = vouchers.length > DEFAULT_VISIBLE_COUNT;
  const displayed = expanded ? vouchers : vouchers.slice(0, DEFAULT_VISIBLE_COUNT);

  return (
    <section className="hh-voucher">
      {/* 模块标题 */}
      <div className="hh-voucher__head">
        <h2 className="hh-voucher__title">
          <span className="hh-voucher__title--accent">特价</span>
          <span className="hh-voucher__title--main">预售</span>
        </h2>
        <div className="hh-voucher__meta">
          <span className="hh-voucher__meta-item">
            <CheckCircleIcon />
            不约可退
          </span>
          <span className="hh-voucher__meta-item">
            <CheckCircleIcon />
            随时退 · 过期退
          </span>
        </div>
      </div>

      {/* 卡片列表 */}
      <div className="hh-voucher__list">
        {displayed.map((voucher) => (
          <VoucherCard key={voucher.id} voucher={voucher} />
        ))}
      </div>

      {/* 展开/收起 */}
      {hasMore && (
        <button
          type="button"
          className="hh-voucher__more"
          onClick={() => {
            // 仅「展开」时上报埋点(收起不上报)
            if (!expanded) point({ page: pageName, action: '展开全部房券' });
            setExpanded((v) => !v);
          }}
        >
          {expanded ? '收起' : '展开全部预售'}
          {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </button>
      )}
    </section>
  );
}
